'use client'

import { FC, memo, useCallback } from 'react'
import { Inventory, SailPackage, SailRef, VoyageInfo } from '@/root/shared-types'
import { dateFormats } from '@/root/shared-consts'
import { dateToFormat } from '@/root/shared-helpers/date/dateHelpers'
import DayPicker from '@/root/components/shared/DayPicker/DayPicker'
import { RouteSection } from '@/root/modules/tuuleliinid/SelectDateTimePage/components/RouteSection/RouteSection'
import { CalendarIcon } from '@/root/ui/tuuleliinid/icons/CalendarIcon'
import { ClockIcon } from '@/root/ui/shared/icons/ClockIcon'
import type { Key } from '@/root/shared-hooks/useGetTranslations/useGetTranslations'
import { getInsufficientCapacityMessageKey, showWarningToast } from '@/root/modules/shared/EditReservation/utils'
import { testIds } from '@/root/shared-consts'

interface JourneySectionProps {
  locale: string
  sailPackage: SailPackage
  sailRef: SailRef
  selectedDate: Date
  onDayClick: (date: Date) => void
  isDayDisabled: (day: Date) => boolean
  time: string
  routeInfo: VoyageInfo
  modifiedInventory: Inventory
  isInventoryLoading: boolean
  selectedSailRefId: number
  setRefIdAndTime: (sailRefId: number, time: string, isNextDay: boolean) => void
  routeTitle: string
  isSaved: boolean
  getTranslation: (key: Key) => string
  requiredPassengers: number
  requiredResidentPassengers: number
  requiredVehicles: number
  requiredResidentVehicles: number
  requiredBicycles: number
  disabled?: boolean
  isSaving?: boolean
  testId?: string
}

export const JourneySection: FC<JourneySectionProps> = memo(({
  locale,
  sailPackage,
  sailRef,
  selectedDate,
  onDayClick,
  isDayDisabled,
  time,
  routeInfo,
  modifiedInventory,
  isInventoryLoading,
  selectedSailRefId,
  setRefIdAndTime,
  routeTitle,
  isSaved,
  getTranslation,
  requiredPassengers,
  requiredResidentPassengers,
  requiredVehicles,
  requiredResidentVehicles,
  requiredBicycles,
  disabled,
  isSaving,
  testId,
}) => {
  const handleInsufficientCapacityClick = useCallback(
    (reasons: string[]) => {
      const messageKey = getInsufficientCapacityMessageKey(reasons)
      if (messageKey) showWarningToast(getTranslation(messageKey))
    },
    [getTranslation]
  )

  return (
  <div data-testid={testId} className="flex flex-col gap-6">
    <div className="flex flex-col gap-4 md:flex-row md:gap-6">
      <div className="w-full max-w-md rounded-2xl bg-white p-4">
        <DayPicker selectedDate={selectedDate} onDayClick={onDayClick} isDayDisabled={isDayDisabled} />
      </div>

      <div className="flex flex-col gap-2">
        <div className="text-primary-default flex items-center gap-2">
          <h2 data-testid={testIds.edit.routeName} className="text-xl font-bold">{routeTitle}</h2>
        </div>
        {isSaved && (
          <span className="text-sm font-semibold text-neutral-darkest">{getTranslation('editNewSelectedDeparture')}</span>
        )}
        <div className={`flex items-center gap-2 text-lg ${isSaved ? 'rounded-lg bg-success-very-light px-3 py-1 font-semibold text-success-darker' : 'text-neutral-darker'}`}>
          <CalendarIcon width={20} height={20} color={isSaved ? 'success-medium-light' : 'primary-base'} />
          <span data-testid={testIds.edit.selectedDate}>{dateToFormat(sailRef.departureDate, dateFormats.WEEKDAY_DATE, locale)}</span>
        </div>
        <div className={`flex items-center gap-2 text-lg ${isSaved ? 'rounded-lg bg-success-very-light px-3 py-1 font-semibold text-success-darker' : 'text-neutral-darker'}`}>
          <ClockIcon width={20} height={20} color={isSaved ? 'success-medium-light' : 'primary-base'} />
          <span data-testid={testIds.edit.departureTime}>{sailRef.departureTime}</span>
        </div>
      </div>
    </div>

    <RouteSection
      routeInfo={routeInfo}
      inventory={modifiedInventory}
      isLoading={isInventoryLoading || !!isSaving}
      sailRefId={selectedSailRefId}
      setRefIdAndTime={setRefIdAndTime}
      selectedDate={selectedDate}
      onDayClick={onDayClick}
      isDayDisabled={isDayDisabled}
      time={time}
      routeAttributes={sailPackage.routeAttributes}
      showSwapPorts={false}
      hideHeader
      requiredPassengers={requiredPassengers}
      requiredResidentPassengers={requiredResidentPassengers}
      requiredVehicles={requiredVehicles}
      requiredResidentVehicles={requiredResidentVehicles}
      requiredBicycles={requiredBicycles}
      onInsufficientCapacityClick={handleInsufficientCapacityClick}
      disabled={disabled}
    />
  </div>
  )
})
